import React, { useEffect, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import * as actions from './actions';
import { toastError } from '../../utils';

const durationMap = { short_1hr: 60, short_1_5hr: 90, short_2hr: 120, short_2_5hr: 150, short_3hr: 180 };

const formatTimeframe = (leaveType, leaveTime) => {
  if (!leaveTime || !durationMap[leaveType]) return '';
  const [h, m] = leaveTime.split(':').map(Number);
  const startMin = h * 60 + m;
  const endMin = startMin + durationMap[leaveType];
  const fmt = (mins) => {
    let hr = Math.floor(mins / 60) % 24;
    const mn = mins % 60;
    const ampm = hr >= 12 ? 'PM' : 'AM';
    hr = hr % 12 || 12;
    return `${hr}:${String(mn).padStart(2, '0')} ${ampm}`;
  };
  return `${fmt(startMin)} - ${fmt(endMin)}`;
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function LeaveCalendar({ setLoader, onDateClick, selectedDate, refreshKey, isShortLeave }) {
  const [leaveMap, setLeaveMap] = useState({});

  useEffect(() => {
    loadCalendarData();
  }, [refreshKey]);

  const loadCalendarData = () => {
    setLoader(true);
    actions.getLeaveCalendar()
      .then((res) => {
        setLoader(false);
        const leaves = res.body || [];
        const map = {};
        leaves.forEach((leave) => {
          const dateKey = leave.leaveDate;
          if (!map[dateKey]) map[dateKey] = [];
          map[dateKey].push({
            name: leave.name,
            status: leave.status,
            leaveType: leave.leaveType,
            leaveTime: leave.leaveTime,
          });
        });
        setLeaveMap(map);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.message || 'Failed to load calendar');
      });
  };

  const today = new Date();
  const minClickable = new Date();
  minClickable.setHours(0, 0, 0, 0);

  const maxClickable = new Date();
  maxClickable.setMonth(maxClickable.getMonth() + 3);

  const months = [
    { year: today.getFullYear(), month: today.getMonth() },
    { year: today.getMonth() + 1 > 11 ? today.getFullYear() + 1 : today.getFullYear(), month: (today.getMonth() + 1) % 12 },
  ];

  const getLeaveTypeLabel = (type) => {
    if (type === 'half_day_morning') return 'Morning';
    if (type === 'half_day_afternoon') return 'Afternoon';
    if (type === 'short_1hr') return '1 Hr';
    if (type === 'short_1_5hr') return '1.5 Hrs';
    if (type === 'short_2hr') return '2 Hrs';
    if (type === 'short_2_5hr') return '2.5 Hrs';
    if (type === 'short_3hr') return '3 Hrs';
    return 'Full Day';
  };

  const selectedStr = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
    : null;

  const renderMonth = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const cells = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(<Box key={`empty-${i}`} sx={{ width: '14.28%', p: 0.3 }} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const leaves = leaveMap[dateStr] || [];
      const hasApproved = leaves.some(l => l.status === 'approved');
      const hasPending = leaves.some(l => l.status === 'pending');

      const isSelected = dateStr === selectedStr;
      const isBlocked = leaves.some(l => l.status === 'approved' && l.leaveType === 'full_day');

      const cellDate = new Date(year, month, day);
      cellDate.setHours(0, 0, 0, 0);
      const isClickable = onDateClick && cellDate >= minClickable && cellDate <= maxClickable && !isBlocked;
      const isPast = cellDate < minClickable;

      let bgColor = 'transparent';
      let borderColor = 'transparent';

      if (isSelected) {
        bgColor = 'rgba(108, 99, 255, 0.25)';
        borderColor = '#6C63FF';
      } else if (hasApproved) {
        bgColor = 'rgba(16, 185, 129, 0.18)';
        borderColor = '#10B981';
      } else if (hasPending) {
        bgColor = 'rgba(245, 158, 11, 0.18)';
        borderColor = '#F59E0B';
      }

      const tooltipContent = leaves.length > 0 ? (
        <Box>
          {leaves.map((l, i) => (
            <Typography key={i} sx={{ fontSize: '0.75rem', color: '#F1F5F9' }}>
              {l.name} - {l.leaveTime ? formatTimeframe(l.leaveType, l.leaveTime) : getLeaveTypeLabel(l.leaveType)}
              <span style={{ color: l.status === 'approved' ? '#10B981' : '#F59E0B', marginLeft: 6 }}>
                ({l.status})
              </span>
            </Typography>
          ))}
          {isBlocked && (
            <Typography sx={{ fontSize: '0.7rem', color: '#EF4444', mt: 0.5, fontWeight: 500 }}>
              Cannot apply for this date
            </Typography>
          )}
        </Box>
      ) : '';

      const handleClick = () => {
        if (isClickable) onDateClick(new Date(year, month, day));
      };

      const cell = (
        <Box key={day} sx={{ width: '14.28%', p: 0.3 }}>
          <Box
            onClick={handleClick}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 36,
              borderRadius: '8px',
              backgroundColor: bgColor,
              border: isSelected
                ? '1.5px solid #6C63FF'
                : leaves.length > 0
                ? `1px solid ${borderColor}30`
                : '1px solid transparent',
              cursor: isBlocked ? 'not-allowed' : isClickable ? 'pointer' : 'default',
              opacity: isPast ? 0.35 : isBlocked ? 0.5 : 1,
              transition: 'all 0.15s ease',
              '&:hover': isClickable ? {
                backgroundColor: isSelected ? bgColor : 'rgba(108, 99, 255, 0.10)',
                border: '1px solid rgba(108, 99, 255, 0.4)',
              } : {},
            }}
          >
            <Typography sx={{
              fontSize: '0.8rem',
              color: isSelected ? '#6C63FF' : '#CBD5E1',
              fontWeight: isSelected ? 600 : 400,
            }}>
              {day}
            </Typography>
            {leaves.length > 0 && (
              <Box sx={{
                position: 'absolute', bottom: 2, left: '50%',
                transform: 'translateX(-50%)', display: 'flex', gap: '2px',
              }}>
                {hasApproved && <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: '#10B981' }} />}
                {hasPending && <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: '#F59E0B' }} />}
              </Box>
            )}
          </Box>
        </Box>
      );

      cells.push(
        leaves.length > 0 ? (
          <Tooltip
            key={`tt-${day}`}
            title={tooltipContent}
            arrow
            placement="top"
            slotProps={{
              tooltip: { sx: { backgroundColor: '#1A1D27', border: '1px solid #2E3247', borderRadius: '8px', px: 1.5, py: 1 } },
              arrow: { sx: { color: '#1A1D27', '&::before': { border: '1px solid #2E3247' } } },
            }}
          >
            {cell}
          </Tooltip>
        ) : cell
      );
    }

    return (
      <Box sx={{ flex: 1, minWidth: 250 }}>
        <Typography sx={{ color: '#F1F5F9', fontSize: '0.9rem', fontWeight: 600, mb: 1.5, textAlign: 'center' }}>
          {MONTH_NAMES[month]} {year}
        </Typography>
        <Box sx={{ display: 'flex', mb: 0.5 }}>
          {DAYS.map((d) => (
            <Box key={d} sx={{ width: '14.28%', textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.68rem', color: '#64748B', fontWeight: 600 }}>{d}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>{cells}</Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {renderMonth(months[0].year, months[0].month)}
        <Box sx={{ width: '1px', backgroundColor: '#2E3247', alignSelf: 'stretch', my: 1 }} />
        {renderMonth(months[1].year, months[1].month)}
      </Box>
      <Box sx={{ display: 'flex', gap: 3, mt: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#10B981' }} />
          <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>Approved</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#F59E0B' }} />
          <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>Pending</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(108, 99, 255, 0.4)', border: '1.5px solid #6C63FF' }} />
          <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>Selected</Typography>
        </Box>
      </Box>
    </Box>
  );
}
